export const TOUR_DESCRIPTIONS: Record<string, string> = {
  // ── Author Menus ──────────────────────────────────────────
  'author-dashboard':
    'Your home dashboard. See counts of total, under-review, approved and rejected submissions, plus your three most recent entries with their current status and a shortcut to start a new submission.',

  'create-manuscript':
    'Submit a new  manuscript through a step-by-step form. Enter the title, list the lead author with co-authors and their affiliations, choose categories, paste the abstract, add ISBN, edition and keywords, then upload the manuscript file.',

  'submission-journal':
    'Apply for TETFund Journal Support. Fill out the editorial board (up to ten members), enter journal metadata like volume, issue, URL and accessibility, attach policy documents, and add your professional association details.',

  'my-submissions':
    "Track all your submissions in two tabs. 'Active Submissions' lists everything currently in the pipeline with its status badge, and 'Returned Submissions' lets you open returned items in a drawer and resubmit the revised version.",

  'library':
    'Browse the TETFund resource library. Filter approved books and journals by type, category, ISBN or keyword and download the full publications along with their metadata.',

  // ── Admin / Shared Header ─────────────────────────────────
  'notifications':
    'Your notification inbox. View system alerts and full HTML email messages from the platform, open any notification for the full body, mark items as read, and tell at a glance what type each one is from its icon.',

  'profile':
    'View and edit your personal profile. Update your name, email, phone, titles, qualifications and rank, pick your institution by category and public/private type, set your address and state, and track how complete your profile is.',

  'profile-reviewer':
    'View and edit your personal profile. Update your name, email, phone, titles, qualifications and rank, pick your institution by category and public/private type, set your address and state, and track how complete your profile is. Your profile also includes a Bank Details tab where you can add or update the account that receives your reviewer payments.',

  'change-password':
    'Change the password you use to sign in. Enter your current password to confirm it is you, then set a new password and save the change.',

  'sign-out':
    'Sign out of your account on this device. The next time you open the platform you will need to sign back in with your email and password.',

  // ── Admin Sidebar Groups ───────────────────────────────────
  'group-settings':
    'System configuration tools. Set up sidebar menus and role access, control event cycles, define thematic areas, manage registered institutions, and review the audit log.',

  'group-users':
    'Manage who can use the platform. Create user accounts, assign roles, designate category admins for each thematic area, and browse the reviewer directory.',

  'group-submissions':
    'Work on incoming manuscript and journal submissions. View those queued at your desk, those across every desk, and the reviewed and revised batches you have access to.',

  'group-assignments':
    'Run the reviewer assignment workflow. Match submissions to reviewers, monitor each assignment through to completion, and record the resulting review decision.',

  'group-reports':
    'View structured reports across every assessment stage (prima facie, reviewer evaluation, journal assessment and revision assessment), and run global searches across submissions and reviewed manuscripts.',

  'group-assessments':
    'Carry out and review every assessment stage (prima facie, reviewer evaluation, journal impact, revision, collective and final), plus the approval chain, author replies and internal panel chats.',

  // ── Admin Specific Pages (by route) ───────────────────────
  'admin/submissions':
    'Browse every thematic area submission in one paginated table. Columns show the ID, title and author, category, current status and the originality (similarity) score; open a row for full details or start an internal chat with unread indicators.',

  'admin/submissions-desk':
    "Submissions queued at your desk, showing each one's prima facie status and decision badge. From here you can move it on to another desk, assign or reassign a prima facie panel, push it back to the author or start an internal panel chat.",

  'admin/setup-menu':
    "Two tabs for configuring the sidebar. 'Menu Management' lets you add, edit and toggle menu items with their route and group, and 'Role Access Management' lets you grant each admin role access to specific menus.",

  'admin/event-cycle':
    'Run the cycle/event grid. Create, edit or delete TETFund cycles and mark one as active, then toggle which events are open within each cycle and set their start and end dates from the side panel.',

  'admin/audit-logs':
    'Read-only audit log of every system action. Search and filter by entity, user, action type, date range or correlation ID, and hover any row to preview the old-vs-new JSON values that the action changed.',

  'admin/manage-users':
    "Two tabs for user administration. 'User Management' lets you add, edit, deactivate or filter users by role, status and date, and 'Category Admin Management' lets you designate the thematic-area lead for each category.",

  'admin/thematic-areas':
    'A simple create/edit/delete table for thematic areas. Give each area a name and a two-character code, and use the inline search to find an existing one fast.',

  'admin/reviewers':
    'Directory of every registered reviewer. Open a reviewer to view their profile, thematic specialisation and assignment history, with summary statistics across the pool.',

  'admin/reviewer-assignment':
    'Match submissions ready for review with qualified reviewers. Pick reviewers manually or let the system auto-assign, set the review deadline, and reassign a manuscript to a different reviewer when needed.',

  'admin/manuscript-reviews':
    "Two tabs covering your review workload. 'My Assignments' shows manuscripts currently assigned to you with their status badge, and 'Returned Reviewed Manuscripts' lists items that came back to you for re-review.",

  'admin/review-decision':
    'Issue the final editorial verdict on reviewed manuscripts. Filter the table by cycle, category and application type, allow or disallow each one, and export the dataset to Excel or PDF.',

  'admin/collective-assessment':
    'An aggregate dashboard showing passed and failed counts across all reviewer evaluations. Slide the pass threshold up or down to see how it shifts the publishable list before committing to a final decision.',

  'admin/final-assessment':
    'Record the final approval or rejection for each manuscript. See applicant details, scores and the geo-zone column at a glance, and filter the list by cycle or category from the dropdowns.',

  'admin/author-communications':
    'Compose a bulk email to authors filtered by assessment outcome (passed or failed). Write your subject and body, attach files, and send it out as the formal communication of record.',

  'admin/internal-communications':
    'Real-time chat for the prima facie panel. Pick a submission, load the full message history, and send messages back and forth with specific roles or recipients over a live SignalR connection.',

  'admin/tetfund-approval':
    "Step a submission through TETFund's seven-stage approval chain. Each row shows which role has approved, which is acting now and which are still pending, with the approver's name displayed at each stage.",

  'admin/prima-facie-assignments':
    "View submissions assigned to you for prima facie screening. Search and page through the list, then click 'Review' on any row to open the assessment form.",

  'admin/prima-facie-reports':
    'Track every prima facie assessment with its panel members and completion status. Open the internal chat drawer, record the decision, or move the submission on to the next desk.',

  'admin/reviewer-evaluation-assignments':
    "View submissions assigned to you for full reviewer evaluation. Search and page through the list, then click 'Review' on any row to start scoring.",

  'admin/reviewer-evaluation-reports':
    'Review every reviewer-evaluation report with its completion status. Record the final decision, manage who sits on each panel, and move the submission to the next desk once conditions are met.',

  'admin/journal-assessment-assignments':
    "View journal applications assigned to you for impact assessment. Search and page through the list, then click 'Review' on any row to open the assessment form.",

  'admin/journal-assessment-reports':
    "Track every journal impact assessment with the convener's status badge. Record the final outcome from the decision modal and view, add or remove the panel members assessing each one.",

  'admin/revised-submissions':
    'Revised manuscripts at your desk. See whether each revision was requested or already received, click through to download the revised copy, and open the side drawer for the full revision history.',

  'admin/revision-assessments':
    "Manuscripts assigned to you for revision assessment. Open each row in the assessment modal to decide whether the author addressed the reviewers' comments, and accept or reject with a justification.",

  'admin/revision-assessment-reports':
    'Track every revision assessment with its completion status. Manage the assessing panel members and mark each submission as acceptable or in need of further processing.',

  'admin/reviewed-manuscripts-desk':
    'Reviewed manuscripts queued at your desk with their decision badge (acceptable, not acceptable, return to reviewer or return for revision). From here you can move each one onward or change its status from the action modals.',

  'admin/search-submission':
    'Global submission search. Type a query for live results showing the ID, title, author, status and exact workflow stage, then click through to the submission record.',

  'admin/search-reviewed-manuscript':
    'Search across reviewed manuscripts. Type a query and get results listing the ID, title, author, current status and which desk the manuscript is now sitting on.',

  'admin/manage-institution':
    'Create, edit and delete the institutions on the platform (universities, polytechnics and colleges), filtering by category, public/private status, state or free-text search across pages of fifty rows.',

  'admin/journals-desk':
    "Journal applications queued at your desk. See each one's title, author, category, status and decision badge, then move it to another desk or message the applicant about its status.",

  'admin/all-submissions':
    'Every submission in the system in a single table. Filter by status, current workflow stage or category and see the full lifecycle including the prima facie outcome and the final decision.',

  'admin/all-revised-submissions':
    "Every revised submission across the platform. Filter by category and read off each one's lead author and current status badge (pending, submitted, acceptable or rejected).",

  'admin/all-reviewed-manuscripts':
    'Every reviewed manuscript in the system. Filter by category and see the decision badge alongside the reviewer assessment status for each one.',
};
